import { suiteColor } from "@/lib/theme";
import { SmsCrediti } from "@/components/SmsCrediti";
import { NovitaDialog } from "@/components/NovitaDialog";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { AppIcon } from "@/components/AppIcon";
import OperatorHome from "@/components/OperatorHome";
import RecentActivityPanel from "@/components/RecentActivityPanel";
import AgencyAlerts from "@/components/AgencyAlerts";
import { ExternalLink, ArrowRight, LayoutGrid, Wrench, HeartHandshake, Building2, Sparkles, X, RefreshCw } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(null);
  const [newVersion, setNewVersion] = useState(false);

  useEffect(() => {
    api.get("/apps/sections").then(({ data }) => setSections(data)).catch(() => setSections({}));
    api
      .get("/apps")
      .then(({ data }) => setApps(data))
      .catch(() => setApps([]))
      .finally(() => setLoading(false));
    api
      .get("/app/revision")
      .then(({ data }) => {
        setRevision(data);
        try {
          const seen = parseInt(localStorage.getItem("assietica_seen_rev") || "0", 10);
          if (seen && data.rev > seen) setNewVersion(true);
          if (!seen) localStorage.setItem("assietica_seen_rev", String(data.rev));
        } catch { /* localStorage non disponibile */ }
      })
      .catch(() => setRevision(null));
  }, []);

  const dismissNewVersion = () => {
    try { if (revision) localStorage.setItem("assietica_seen_rev", String(revision.rev)); } catch { /* noop */ }
    setNewVersion(false);
  };

  const openApp = async (app) => {
    if (app.__wip) { navigate("/work-in-progress"); return; }
    try {
      await api.post("/logs/access", { app_id: app.app_id });
    } catch {
      /* logging non blocca l'apertura */
    }
    if (app.app_type === "integrated") {
      navigate(`/app/${app.app_id}`);
    } else if (app.app_type === "native") {
      navigate(app.route || "/mup");
    } else {
      window.open(app.url, "_blank", "noopener,noreferrer");
    }
  };

  const CONTAINER_KEYS = ["strumenti_digitali", "contabilita_coop", "direzionale"];
  const KNOWN_SECTIONS = ["wip", "benefit", "gestione_agenzia", ...CONTAINER_KEYS];
  const sectionOf = (a) => {
    const s = a.section || "operative";
    return KNOWN_SECTIONS.includes(s) ? s : "operative";
  };
  const liveApps = apps.filter((a) => sectionOf(a) === "operative");
  const wipApps = apps.filter((a) => sectionOf(a) === "wip");
  const benefitApps = apps.filter((a) => sectionOf(a) === "benefit");
  const gaApps = apps.filter((a) => sectionOf(a) === "gestione_agenzia");
  const containerCount = (key) => apps.filter((a) => sectionOf(a) === key).length;
  const isInternal = user?.role === "admin" || apps.some((a) => !a.benefit_ets);

  // Ordine fisso definitivo delle schede in Dashboard
  const CARD_ORDER = {
    "__contabilita_coop__": 1, "__strumenti_digitali__": 2, "__direzionale__": 3, "__ga__": 4,
    "Gestione Sinistri": 5, "MUP Assietica": 6,
    "__benefit__": 10, "Area Riservata": 11, "__wip__": 12,
  };
  const CONTAINER_META = {
    strumenti_digitali: { label: "Strumenti Digitali", icon: "Sparkles", color: "#7C3AED", route: "/strumenti-digitali" },
    contabilita_coop: { label: "Contabilità Cooperativa", icon: "Wallet", color: "#0D9488", route: "/contabilita-cooperativa" },
    direzionale: { label: "Direzionale", icon: "BarChart3", color: "#1B2380", route: "/direzionale" },
  };
  const orderedTiles = () => {
    const tiles = liveApps.map((app) => ({ order: CARD_ORDER[app.name] ?? 90, kind: "app", app }));
    CONTAINER_KEYS.forEach((key) => {
      if (containerCount(key) > 0) tiles.push({ order: CARD_ORDER[`__${key}__`] ?? 90, kind: "container", key });
    });
    if (gaApps.length > 0) tiles.push({ order: CARD_ORDER.__ga__, kind: "ga" });
    if (benefitApps.length > 0) tiles.push({ order: CARD_ORDER.__benefit__, kind: "benefit" });
    if (wipApps.length > 0) tiles.push({ order: CARD_ORDER.__wip__, kind: "wip" });
    return tiles.sort((a, b) => a.order - b.order);
  };

  const renderContainerCard = (key, i) => {
    const m = { ...CONTAINER_META[key], ...(sections[key] || {}) };
    const accent = suiteColor(m.color) || "#1B2380";
    return (
      <motion.button
        key={`container-${key}`}
        data-testid={`container-card-${key}`}
        onClick={() => navigate(CONTAINER_META[key].route)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: i * 0.06 }}
        className="group relative text-left rounded-xl bg-white border border-slate-200 p-6 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
        style={{ borderColor: undefined }}
      >
        <div className="h-12 w-12 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: `${accent}1A`, color: accent }}>
          <AppIcon name={m.icon} className="h-6 w-6" />
        </div>
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <h3 className="font-heading text-lg font-medium text-slate-900">{m.label}</h3>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: `${accent}1A`, color: accent }}>{containerCount(key)}</span>
        </div>
        <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 min-h-[2.5rem]">{m.description || "Apri per vedere gli strumenti raggruppati."}</p>
        <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-slate-400 group-hover:text-brand-blue transition-colors">
          Apri <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </motion.button>
    );
  };

  const renderCard = (app, i, wip = false) => (
    <motion.button
      key={app.app_id}
      data-testid={`app-tile-${app.app_id}`}
      onClick={() => openApp(app)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: i * 0.06 }}
      className={`group relative text-left rounded-xl bg-white border border-slate-200 p-6 shadow-sm hover:border-slate-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 ${wip ? "border-dashed" : ""}`}
    >
      <div
        className="h-12 w-12 rounded-lg flex items-center justify-center mb-5"
        style={{ backgroundColor: `${suiteColor(app.color)}1A`, color: suiteColor(app.color) }}
      >
        <AppIcon name={app.icon} className="h-6 w-6" />
      </div>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <h3 className="font-heading text-lg font-medium text-slate-900">{app.name}</h3>
        {wip && (
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold" data-testid="app-wip-badge">
            <Wrench className="h-3 w-3" /> WIP
          </span>
        )}
        {app.app_type === "external" ? (
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold" data-testid="app-external-badge">
            <ExternalLink className="h-3 w-3" /> Esterno
          </span>
        ) : app.app_type === "native" ? (
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-blue/15 text-brand-blue">
            nativa
          </span>
        ) : (
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-green/15 text-brand-green">
            integrata
          </span>
        )}
        {app.benefit_ets && (
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold" data-testid="app-benefit-badge">
            Benefit ETS
          </span>
        )}
      </div>
      <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 min-h-[2.5rem]">
        {app.description}
      </p>
      <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-slate-400 group-hover:text-brand-blue transition-colors">
        {app.app_type === "external" ? "Apri in nuova scheda" : "Apri"} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
      </div>
    </motion.button>
  );

  const renderWipCard = (i) => (
    <motion.button
      key="wip-card"
      data-testid="wip-card"
      onClick={() => navigate("/work-in-progress")}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: i * 0.06 }}
      className="group relative text-left rounded-xl bg-white border-2 border-dashed border-orange-300 p-6 shadow-sm hover:border-orange-400 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/10 transition-all duration-300"
    >
      <div className="h-12 w-12 rounded-lg flex items-center justify-center mb-5 bg-orange-100 text-orange-600"><Wrench className="h-6 w-6" /></div>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <h3 className="font-heading text-lg font-medium text-slate-900">Work in Progress</h3>
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold">{wipApps.length}</span>
      </div>
      <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 min-h-[2.5rem]">Moduli in fase di completamento. Apri per vedere le app in lavorazione.</p>
      <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-slate-400 group-hover:text-orange-500 transition-colors">Apri <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
    </motion.button>
  );

  const renderBenefitCard = (i) => (
    <motion.button
      key="benefit-card"
      data-testid="benefit-card"
      onClick={() => navigate("/progetti-benefit")}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: i * 0.06 }}
      className="group relative text-left rounded-xl bg-white border-2 border-dashed border-violet-300 p-6 shadow-sm hover:border-violet-400 hover:-translate-y-1 hover:shadow-xl hover:shadow-violet-500/10 transition-all duration-300"
    >
      <div className="h-12 w-12 rounded-lg flex items-center justify-center mb-5 bg-violet-100 text-violet-600"><HeartHandshake className="h-6 w-6" /></div>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <h3 className="font-heading text-lg font-medium text-slate-900">Progetti Benefit</h3>
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold">{benefitApps.length}</span>
      </div>
      <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 min-h-[2.5rem]">Servizi dedicati agli enti Benefit ETS. Apri per gestire i progetti.</p>
      <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-slate-400 group-hover:text-violet-500 transition-colors">Apri <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
    </motion.button>
  );

  const renderGaCard = (i) => (
    <motion.button
      key="ga-card"
      data-testid="gestione-agenzia-card"
      onClick={() => navigate("/gestione-agenzia")}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: i * 0.06 }}
      className="group relative text-left rounded-xl bg-white border border-slate-200 p-6 shadow-sm hover:border-indigo-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-indigo-500/10 transition-all duration-300"
    >
      <div className="h-12 w-12 rounded-lg flex items-center justify-center mb-5 bg-indigo-100 text-indigo-600"><Building2 className="h-6 w-6" /></div>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <h3 className="font-heading text-lg font-medium text-slate-900">Gestione Agenzia</h3>
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">{gaApps.length}</span>
      </div>
      <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 min-h-[2.5rem]">Personale, compliance e attività amministrative interne dell'agenzia.</p>
      <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-slate-400 group-hover:text-indigo-500 transition-colors">Apri <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
    </motion.button>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <SmsCrediti />
        {!loading && isInternal && <NovitaDialog />}
        {newVersion && revision && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3"
            data-testid="app-new-version-alert"
          >
            <Sparkles className="h-5 w-5 text-emerald-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-emerald-800">Nuova versione disponibile — rev. {revision.rev}</p>
              <p className="text-xs text-emerald-700">L'app è stata aggiornata. Ricarica la pagina per usare l'ultima versione.</p>
            </div>
            <button onClick={() => window.location.reload()} data-testid="app-new-version-reload"
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 transition-colors">
              <RefreshCw className="h-4 w-4" /> Ricarica
            </button>
            <button onClick={dismissNewVersion} data-testid="app-new-version-dismiss"
              className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-100 transition-colors" title="Ignora">
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-10"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">App Launcher</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">
            Ciao, {user?.name?.split(" ")[0] || "benvenuto"}
          </h1>
          <p className="text-slate-500 mt-2">
            Le tue applicazioni autorizzate. Un solo accesso per tutta l'agenzia.
          </p>
          {revision && (
            <p className="text-xs text-slate-400 mt-3 font-mono" data-testid="app-revision">
              rev. {revision.rev}, {new Date(revision.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}
            </p>
          )}
        </motion.div>

        {isInternal && <OperatorHome />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonna sinistra: accesso alle app */}
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">Le tue app</p>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-44 rounded-xl bg-white border border-slate-200 animate-pulse" />
                ))}
              </div>
            ) : apps.length === 0 ? (
              <div
                data-testid="empty-apps-state"
                className="flex flex-col items-center justify-center text-center py-24 rounded-2xl border border-dashed border-slate-300 bg-white"
              >
                <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <LayoutGrid className="h-6 w-6 text-slate-400" />
                </div>
                <h3 className="font-heading text-xl text-slate-900 mb-1">Nessuna app disponibile</h3>
                <p className="text-slate-500 max-w-sm">
                  Non ti è ancora stata assegnata alcuna applicazione. Contatta l'amministratore per ottenere l'accesso.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="apps-grid">
                {orderedTiles().map((tile, i) => {
                  if (tile.kind === "app") return renderCard(tile.app, i, false);
                  if (tile.kind === "container") return renderContainerCard(tile.key, i);
                  if (tile.kind === "ga") return renderGaCard(i);
                  if (tile.kind === "benefit") return renderBenefitCard(i);
                  if (tile.kind === "wip") return renderWipCard(i);
                  return null;
                })}
              </div>
            )}
          </div>

          {/* Colonna destra: attività recente (verifica veloce) — solo staff interno */}
          {isInternal && (
            <aside className="lg:col-span-1">
              <AgencyAlerts />
              <RecentActivityPanel />
            </aside>
          )}
        </div>
      </main>
    </div>
  );
}
